import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Star from './star';

class StarRatings extends Component {
  constructor(props) {
    super(props);
    this.state = {
      highestStarHovered: -Infinity,
    };

    // Stable id for the gradient fill (generated once)
    this.fillId = 'starGrad' + Math.random().toFixed(15).slice(2);

    // Bind methods for React 15 compatibility (no class fields syntax required)
    this.unHoverOverStar = this.unHoverOverStar.bind(this);
    this.stopColorStyle = this.stopColorStyle.bind(this);
    this.starRatingsStyle = this.starRatingsStyle.bind(this);
    this.starGradientStyle = this.starGradientStyle.bind(this);
    this.getTitleText = this.getTitleText.bind(this);
    this.getOffsetValue = this.getOffsetValue.bind(this);
    this.renderStars = this.renderStars.bind(this);
  }

  hoverOverStar(starRating) {
    var self = this;
    return function () {
      self.setState({ highestStarHovered: starRating });
    };
  }

  unHoverOverStar() {
    this.setState({ highestStarHovered: -Infinity });
  }

  stopColorStyle(color) {
    return this.props.ignoreInlineStyles ? {} : { stopColor: color, stopOpacity: '1' };
  }

  starRatingsStyle() {
    return this.props.ignoreInlineStyles
      ? {}
      : {
          position: 'relative',
          boxSizing: 'border-box',
          display: 'inline-block',
        };
  }

  starGradientStyle() {
    return this.props.ignoreInlineStyles
      ? {}
      : {
          position: 'absolute',
          zIndex: 0,
          width: 0,
          height: 0,
          visibility: 'hidden',
        };
  }

  getTitleText() {
    const { rating, typeOfWidget } = this.props;
    const hoveredRating = this.state.highestStarHovered;
    const currentRating = hoveredRating > 0 ? hoveredRating : rating;

    let formatted = parseFloat(currentRating.toFixed(2)).toString();
    if (Math.floor(currentRating) === currentRating) formatted = String(currentRating);

    const noun = formatted === '1' ? typeOfWidget : `${typeOfWidget}s`;
    return `${formatted} ${noun}`;
  }

  getOffsetValue() {
    const { rating } = this.props;
    if (Math.floor(rating) === rating) return '0%';
    const firstTwoDecimals = rating.toFixed(2).split('.')[1].slice(0, 2);
    return `${firstTwoDecimals}%`;
  }

  renderStars() {
    const {
      numberOfStars,
      rating,
      changeRating,
      starDimension,
      starSpacing,
      starHoverColor,
      starRatedColor,
      starEmptyColor,
      gradientPathName,
      ignoreInlineStyles,
      svgIconPath,
      svgIconViewBox,
    } = this.props;

    const { highestStarHovered } = this.state;

    const stars = [];
    for (let index = 0; index < numberOfStars; index += 1) {
      const starRating = index + 1;
      const isStarred = starRating <= rating;

      const hoverMode = highestStarHovered > 0;
      const isHovered = starRating <= highestStarHovered;
      const isCurrentHoveredStar = starRating === highestStarHovered;

      const isPartiallyFullStar = starRating > rating && starRating - 1 < rating;

      const isFirstStar = starRating === 1;
      const isLastStar = starRating === numberOfStars;

      stars.push(
        <Star
          key={starRating}
          fillId={this.fillId}
          changeRating={changeRating ? (e) => changeRating(starRating, e) : null}
          hoverOverStar={changeRating ? this.hoverOverStar(starRating) : null}
          unHoverOverStar={changeRating ? this.unHoverOverStar : null}
          isStarred={isStarred}
          isPartiallyFullStar={isPartiallyFullStar}
          isHovered={isHovered}
          hoverMode={hoverMode}
          isCurrentHoveredStar={isCurrentHoveredStar}
          isFirstStar={isFirstStar}
          isLastStar={isLastStar}
          starDimension={starDimension}
          starSpacing={starSpacing}
          starHoverColor={starHoverColor}
          starRatedColor={starRatedColor}
          starEmptyColor={starEmptyColor}
          gradientPathName={gradientPathName}
          ignoreInlineStyles={ignoreInlineStyles}
          svgIconPath={svgIconPath}
          svgIconViewBox={svgIconViewBox}
        />
      );
    }
    return stars;
  }

  render() {
    const titleText = this.getTitleText();
    const offsetValue = this.getOffsetValue();

    return (
      <div className="star-ratings" title={titleText} style={this.starRatingsStyle()}>
        {/* Hidden gradient definition used by Star children */}
        <svg className="star-grad" style={this.starGradientStyle()}>
          <defs>
            <linearGradient id={this.fillId} x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" className="stop-color-first" style={this.stopColorStyle(this.props.starRatedColor)} />
              <stop offset={offsetValue} className="stop-color-first" style={this.stopColorStyle(this.props.starRatedColor)} />
              <stop offset={offsetValue} className="stop-color-final" style={this.stopColorStyle(this.props.starEmptyColor)} />
              <stop offset="100%" className="stop-color-final" style={this.stopColorStyle(this.props.starEmptyColor)} />
            </linearGradient>
          </defs>
        </svg>

        {this.renderStars()}
      </div>
    );
  }
}

StarRatings.propTypes = {
  rating: PropTypes.number.isRequired,
  numberOfStars: PropTypes.number.isRequired,
  changeRating: PropTypes.func,
  starHoverColor: PropTypes.string.isRequired,
  starRatedColor: PropTypes.string.isRequired,
  starEmptyColor: PropTypes.string.isRequired,
  starDimension: PropTypes.string.isRequired,
  starSpacing: PropTypes.string.isRequired,
  gradientPathName: PropTypes.string.isRequired,
  ignoreInlineStyles: PropTypes.bool.isRequired,
  svgIconPath: PropTypes.string.isRequired,
  svgIconViewBox: PropTypes.string.isRequired,
  typeOfWidget: PropTypes.string,
};

StarRatings.defaultProps = {
  rating: 0,
  typeOfWidget: 'Star',
  numberOfStars: 5,
  changeRating: null,
  starHoverColor: 'rgb(230, 67, 47)',
  starRatedColor: 'rgb(109, 122, 130)',
  starEmptyColor: 'rgb(203, 211, 227)',
  starDimension: '50px',
  starSpacing: '7px',
  gradientPathName: '',
  ignoreInlineStyles: false,
  svgIconPath: 'm25,1 6,17h18l-14,11 5,17-15-10-15,10 5-17-14-11h18z',
  svgIconViewBox: '0 0 51 48',
};

export default StarRatings;
